import { Component, ElementRef, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from 'src/app/services/api.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'meter-bulk-update-modal',
  templateUrl: './meter-bulk-update.component.html',
  styleUrls: ['./meter-bulk-update.component.scss']
})
export class MeterBulkUpdateDialog {
  selectedFile: File | null = null;
  isDraggingOver = false;
  uploading = false;
  uploadResult: any = null;
  parsedMeters: any[] = [];
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    public dialogRef: MatDialogRef<MeterBulkUpdateDialog>,
    private apiService: ApiService,
    private snackBar: MatSnackBar
  ) {}

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.handleFile(file);
    }
  }

  handleFile(file: File): void {
    const isValid = file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx') ||
      ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'csv'].includes(file.type);
    if (!isValid) {
      this.snackBar.open('Please select a valid CSV, XLS or XLSX file', 'Close', { duration: 3000 });
      this.resetFile();
      return;
    }
    this.selectedFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array', cellDates: true });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      this.parsedMeters = this.parseRows(rows);
      if (this.parsedMeters.length === 0) {
        this.snackBar.open('No valid meter rows found in file', 'Close', { duration: 3000 });
        this.resetFile();
      } else {
        this.snackBar.open(`${this.parsedMeters.length} meter(s) parsed`, 'Close', { duration: 2000 });
      }
    };
    reader.readAsArrayBuffer(file);
  }

  parseRows(rows: any[][]): any[] {
    if (rows.length < 2) {
      return [];
    }
    const headers = rows[0].map((h: any) => String(h).trim().toLowerCase().replace(/\s+/g, '_'));
    const meters: any[] = [];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.length === 0) {
        continue;
      }
      const rowMap: any = {};
      headers.forEach((h: string, idx: number) => {
        rowMap[h] = row[idx];
      });
      const meterId = this.toLong(rowMap['meter_id']);
      if (!meterId) {
        continue;
      }
      const meter = {
        meterId: meterId,
        currentReading: this.toLong(rowMap['curr_reading']),
        customerId: this.toLong(rowMap['cust_id']),
        meterStatus: this.toString(rowMap['meter_status']).toLowerCase(),
        serialNumber: this.toStringNullable(rowMap['serial_number']),
        previousReading: this.toLong(rowMap['prev_reading']),
        qiyaasReading: this.toLong(rowMap['qiyaas_reading'])
      };
      meters.push(meter);
    }
    return meters;
  }

  onUpload(): void {
    if (!this.parsedMeters.length) {
      this.snackBar.open('Please select a file with meter data', 'Close', { duration: 3000 });
      return;
    }
    this.uploading = true;
    this.apiService.bulkUpdateMeters(this.parsedMeters).subscribe({
      next: (result) => {
        this.uploading = false;
        this.uploadResult = result;
        this.resetFile();
        this.snackBar.open(`Update complete: ${result.updatedCount} updated, ${result.failedCount} failed`, 'Close', { duration: 5000 });
      },
      error: (err) => {
        this.uploading = false;
        this.snackBar.open('Update failed: ' + (err.error?.message || err.message), 'Close', { duration: 5000 });
      }
    });
  }

  done(): void {
    this.dialogRef.close(this.uploadResult && this.uploadResult.updatedCount > 0);
  }

  uploadAnother(): void {
    this.resetFile();
    this.uploadResult = null;
  }

  private toString(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value).trim();
  }

  private toStringNullable(value: any): string | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const s = String(value).trim().toLowerCase();
    if (s === 'null' || s === 'nil') {
      return null;
    }
    return String(value).trim();
  }

  private toLong(value: any): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const s = String(value).trim().toLowerCase();
    if (s === 'null') {
      return null;
    }
    const n = Number(s);
    return isNaN(n) ? null : n;
  }

  private resetFile(): void {
    this.selectedFile = null;
    this.parsedMeters = [];
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
